import { useState, type FC } from 'react';
import type { StudentLiteratureItem, StudentSavedLiterature } from '../types';
import { DownloadIcon, SearchIcon, StarIcon } from './CourseStudentIcons';

interface StudentLiteratureOverlayProps {
  savedList: StudentSavedLiterature[];
  onSearch: (req: {
    topic: string;
    discipline: string;
    source_type: string;
    period: string;
    language: string;
    databases: string[];
    full_text_only: boolean;
    limit: number;
    extra_criteria?: string;
  }) => Promise<StudentLiteratureItem[]>;
  onSaveLiterature: (item: StudentLiteratureItem) => Promise<void>;
  onDeleteSaved: (id: string) => Promise<void>;
  onBackToCabinet: () => void;
}

export const StudentLiteratureOverlay: FC<StudentLiteratureOverlayProps> = ({
  savedList,
  onSearch,
  onSaveLiterature,
  onDeleteSaved,
  onBackToCabinet,
}) => {
  const [activeTab, setActiveTab] = useState<'search' | 'saved'>('search');
  const [topic, setTopic] = useState('дискурс');
  const [discipline, setDiscipline] = useState('Лингвистика');
  const [sourceType, setSourceType] = useState('Статьи + обзоры');
  const [period, setPeriod] = useState('Последние 5 лет');
  const [language, setLanguage] = useState('English + Russian + Kazakh');
  const [databases, setDatabases] = useState<string[]>(['Scopus', 'Web of Science', 'Crossref·DOI']);
  const [fullTextOnly, setFullTextOnly] = useState(true);
  const [limit, setLimit] = useState(10);
  const [extraCriteria, setExtraCriteria] = useState('');
  const [results, setResults] = useState<StudentLiteratureItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const toggleDatabase = (dbName: string) => {
    setDatabases((prev) =>
      prev.includes(dbName) ? prev.filter((d) => d !== dbName) : [...prev, dbName]
    );
  };

  const handleSearch = async () => {
    if (!topic.trim()) return;
    setIsSearching(true);
    try {
      const items = await onSearch({
        topic,
        discipline,
        source_type: sourceType,
        period,
        language,
        databases,
        full_text_only: fullTextOnly,
        limit,
        extra_criteria: extraCriteria,
      });
      setResults(items);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="border border-slate-800 bg-[#081321] rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBackToCabinet}
            className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700/80 hover:border-cyan-400 text-slate-300 hover:text-cyan-400 flex items-center justify-center transition-all"
            aria-label="Вернуться в кабинет"
          >
            &larr;
          </button>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">
              Кабинет обучающегося
            </p>
            <h2 className="text-xl font-bold text-white tracking-wide">Найти литературу</h2>
            <p className="text-xs text-slate-400">
              Поиск литературы и личная сохранённая библиотека
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
              activeTab === 'search'
                ? 'bg-cyan-950 border-cyan-400 text-cyan-300 shadow-md'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Поиск источников
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('saved')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
              activeTab === 'saved'
                ? 'bg-amber-950 border-amber-400 text-amber-300 shadow-md'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Моя библиотека ({savedList.length})
          </button>
        </div>
      </div>

      {activeTab === 'search' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 p-5 rounded-2xl bg-[#081524] border border-cyan-500/30 space-y-4 shadow-xl">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <SearchIcon className="w-5 h-5 text-cyan-400" />
              Параметры академического поиска
            </h3>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Тема / ключевые понятия *
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Например: семантика дискурса"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Дисциплина</label>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
                >
                  <option value="Лингвистика">Лингвистика</option>
                  <option value="Образование">Образование</option>
                  <option value="Филология">Филология</option>
                  <option value="Междисциплинарное исследование">Междисциплинарное исследование</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Тип источника</label>
                <select
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
                >
                  <option value="Статьи + обзоры">Статьи + обзоры</option>
                  <option value="Только статьи">Только статьи</option>
                  <option value="Книги / главы">Книги / главы</option>
                  <option value="Все академические материалы">Все академические материалы</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Период</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
                >
                  <option value="Последние 5 лет">Последние 5 лет</option>
                  <option value="Последние 10 лет">Последние 10 лет</option>
                  <option value="Без ограничения">Без ограничения</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Язык</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
                >
                  <option value="English + Russian + Kazakh">English + Russian + Kazakh</option>
                  <option value="Kazakh">Kazakh</option>
                  <option value="Russian">Russian</option>
                  <option value="English">English</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-2">Академические базы</label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {['Scopus', 'Web of Science', 'Crossref·DOI', 'Open repositories', 'institutional library'].map(
                  (dbName) => (
                    <label key={dbName} className="flex items-center gap-2 text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={databases.includes(dbName)}
                        onChange={() => toggleDatabase(dbName)}
                        className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                      />
                      <span className="text-[11px]">{dbName}</span>
                    </label>
                  )
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fullTextOnly}
                  onChange={(e) => setFullTextOnly(e.target.checked)}
                  className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                />
                <span>Только полный текст (Open Access / Legal PDF)</span>
              </label>

              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Дополнительные ограничения
              </label>
              <textarea
                value={extraCriteria}
                onChange={(e) => setExtraCriteria(e.target.value)}
                placeholder="Например: только рецензируемые издания Q1-Q2"
                rows={2}
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-cyan-400 focus:outline-none"
              />
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full py-2.5 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md shadow-amber-500/10 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <SearchIcon className="w-4 h-4" />
              {isSearching ? 'Выполняется поиск...' : 'Найти литературу'}
            </button>
          </div>

          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white">
                Результаты поиска {results.length > 0 && `(${results.length})`}
              </h3>
              <span className="text-xs text-slate-400">Legal Open Access Only</span>
            </div>

            {results.length === 0 ? (
              <div className="p-8 rounded-2xl bg-[#08121E] border border-slate-800 text-center space-y-2">
                <SearchIcon className="w-10 h-10 text-slate-600 mx-auto" />
                <p className="text-sm text-slate-300 font-semibold">Начните поиск литературы</p>
                <p className="text-xs text-slate-400">
                  Укажите тему и параметры фильтрации, чтобы получить научные статьи, монографии и аналитические обзоры.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((res) => (
                  <div
                    key={res.id}
                    className="p-4 rounded-2xl bg-[#081321] border border-slate-800 hover:border-cyan-500/40 transition-all space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-950 text-cyan-300 border border-cyan-800/60">
                            {res.database_source}
                          </span>
                          {res.is_open_access && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-950 text-emerald-300 border border-emerald-800/60">
                              Open Access
                            </span>
                          )}
                        </div>
                        <h4 className="text-sm font-bold text-white mt-1.5 leading-snug">
                          {res.title}
                        </h4>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {res.authors} · {res.journal} ({res.year})
                        </p>
                        {res.doi && (
                          <p className="text-[11px] font-mono text-cyan-400 mt-0.5">
                            DOI: {res.doi}
                          </p>
                        )}
                      </div>
                    </div>

                    {res.abstract && (
                      <p className="text-xs text-slate-300 line-clamp-3 leading-relaxed">
                        {res.abstract}
                      </p>
                    )}

                    <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => onSaveLiterature(res)}
                        className="px-3.5 py-1.5 rounded-xl border border-amber-500/50 hover:bg-amber-950/40 text-amber-300 text-xs font-semibold transition-all flex items-center gap-1.5"
                      >
                        <StarIcon className="w-3.5 h-3.5" />
                        Сохранить в библиотеку
                      </button>

                      {res.download_url && (
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 text-xs font-medium transition-all flex items-center gap-1.5"
                        >
                          <DownloadIcon className="w-3.5 h-3.5" />
                          PDF
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-white">Моя сохранённая литература</h3>
              <p className="text-xs text-slate-400">
                Личный библиографический список для курсовых, СРО и научных проектов.
              </p>
            </div>
            <span className="text-xs font-bold text-amber-400 px-3 py-1 rounded-xl bg-amber-950/60 border border-amber-800/40">
              Всего: {savedList.length}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {savedList.map((s) => (
              <div
                key={s.id}
                className="p-5 rounded-2xl bg-[#081321] border border-amber-500/30 space-y-3 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-amber-400 uppercase">
                      {s.source_type} · {s.database_source || 'Scopus'}
                    </span>
                    <button
                      type="button"
                      onClick={() => onDeleteSaved(s.id)}
                      className="text-xs text-rose-400 hover:text-rose-300 transition-colors"
                      aria-label="Удалить"
                    >
                      Удалить
                    </button>
                  </div>
                  <h4 className="text-sm font-bold text-white mt-1 leading-snug">{s.title}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {s.authors} {s.journal ? `· ${s.journal}` : ''} ({s.year})
                  </p>
                  {s.doi && (
                    <p className="text-[11px] font-mono text-cyan-400 mt-0.5">DOI: {s.doi}</p>
                  )}
                  {s.notes && (
                    <div className="mt-2 p-2 rounded-lg bg-slate-900/80 border border-slate-800 text-[11px] text-slate-300">
                      <span className="font-semibold text-slate-400">Заметка: </span>
                      {s.notes}
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {(s.tags_json || []).map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-300">
                        #{t}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-300"
                    aria-label="Скачать"
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
